// eslint-disable-next-line jira/filename-case
import { useCallback, useEffect } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useService } from '@jira/platform__use-service/src/useService.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';

const getUserOrgPermissions = async ({ orgId, userId }: { orgId: string; userId: string }) =>
	performPostRequest<{ permitted: boolean } | null>('/gateway/api/permissions/permitted', {
		body: JSON.stringify({
			resourceId: `ari:cloud:platform::org/${orgId}`,
			principalId: `ari:cloud:identity::user/${userId}`,
			permissionId: 'manage/org',
		}),
	}).then((resp) => !!resp?.permitted);

export type UseIsOrgAdmin = {
	isOrgAdmin: boolean;
	loading: boolean;
	hasErrorOccurred: boolean;
	retry: () => void;
};

export const useIsOrgAdmin = (): UseIsOrgAdmin => {
	const userId = useAccountId();
	const orgId = useOrgId();

	const checkUserIsOrgAdmin = useCallback(async () => {
		if (!userId || !orgId) {
			return false;
		}
		return getUserOrgPermissions({ orgId, userId });
	}, [orgId, userId]);

	const {
		loading,
		error,
		data,
		fetch: fetchIsOrgAdmin,
	} = useService<boolean>(checkUserIsOrgAdmin, { data: false });

	useEffect(() => {
		fetchIsOrgAdmin();
	}, [fetchIsOrgAdmin]);

	useEffect(() => {
		fireErrorAnalytics({
			meta: {
				id: 'checkUserIsOrgAdmin',
				packageName: 'jiraPlatformSrerviceIsOrgAdmin',
				teamName: 'duke-dlm',
			},
			sendToPrivacyUnsafeSplunk: true,
			error,
		});
	}, [error]);

	return {
		isOrgAdmin: !!data,
		loading,
		hasErrorOccurred: !!error,
		retry: fetchIsOrgAdmin,
	};
};
